import { type UserSession, userSignsInWithSso, useUserSession } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  Button,
  Card,
  CardHeader,
  Flex,
  PopoverTooltip,
  TextField,
  toast,
  usePopoverTooltip,
} from '@solinkcorp/solink-components';
import UserActions from 'actions/UserActions';
import { CallhomeApi } from 'clients';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { type PlayingCamerasController } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { type AuthSettings } from 'firebase/auth';
import get from 'lodash/get';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, { Component, useContext } from 'react';
import useLocale from 'src/hooks/useLocale';
import ComponentUtil from 'utils/ComponentUtil';
import { getLocalizedStr } from 'utils/LocaleUtil';
import * as constants from 'vd-constants';
import {
  FAST_NOTIFICATION_DURATION,
  PASSWORD_MIN_LENGTH,
  REGEX_INCLUDES_LOWERCASE_LETTER,
  REGEX_INCLUDES_NUMBER,
  REGEX_INCLUDES_SPECIAL_CHARACTER,
  REGEX_INCLUDES_UPPERCASE_LETTER,
} from 'vd-constants';
import Validations from './Validations';

interface MyAccountPageProps {
  locale: LocaleDefinition;
  user: UserSession;
  shouldShowOverridesPage?: boolean;
  playingCamerasController?: PlayingCamerasController;
  authSettings: AuthSettings;
  popover: ReturnType<typeof usePopoverTooltip>;
}

interface MyAccountPageState {
  confirmPassword: string;
  newPassword: string;
  newPasswordError: string | (string | React.ReactElement)[];
  confirmPasswordError: string;
  isMfaEnabled: boolean;
  validPasswordLength: boolean;
  passwordIncludesNumber: boolean;
  passwordIncludesSpecialChar: boolean;
  passwordIncludesUppercase: boolean;
  passwordIncludesLowercase: boolean;
  passwordValidationTooltipVisible: boolean;
}

interface ComposedMyAccountPageProps {
  shouldShowOverridesPage?: boolean;
}

class MyAccountPage extends Component<MyAccountPageProps, MyAccountPageState> {
  constructor(props: MyAccountPageProps) {
    super(props);
    const { user } = props;
    AnalyticsLogger.loadAccountPage();
    this.state = {
      confirmPassword: '',
      newPassword: '',
      newPasswordError: '',
      confirmPasswordError: '',
      isMfaEnabled: user.profile.isMfaEnabled ?? false,
      validPasswordLength: false,
      passwordIncludesNumber: false,
      passwordIncludesSpecialChar: false,
      passwordIncludesUppercase: false,
      passwordIncludesLowercase: false,
      passwordValidationTooltipVisible: false,
    };
  }

  shouldComponentUpdate(nextProps: MyAccountPageProps, nextState: MyAccountPageState) {
    return ComponentUtil.shouldComponentUpdate({ context: this, nextProps, nextState });
  }

  componentDidMount() {
    const { locale } = this.props;
    document.title = `${locale.settingsPage.myAccount} | ${constants.TITLE}`;
  }

  handleNewPasswordChange = ({ value }: { value: string }) => {
    const { authSettings } = this.props;
    const { confirmPassword } = this.state;
    const minPassLength = get(authSettings, 'minPasswordLength', PASSWORD_MIN_LENGTH);

    this.setErrorsConfirmPassword({ confirmPassword, newPassword: value });
    this.setErrorsNewPassword({ newPassword: value });

    this.setState({
      newPassword: value,
      validPasswordLength: value.length >= minPassLength,
      passwordIncludesNumber: REGEX_INCLUDES_NUMBER.test(value),
      passwordIncludesSpecialChar: REGEX_INCLUDES_SPECIAL_CHARACTER.test(value),
      passwordIncludesUppercase: REGEX_INCLUDES_UPPERCASE_LETTER.test(value),
      passwordIncludesLowercase: REGEX_INCLUDES_LOWERCASE_LETTER.test(value),
      passwordValidationTooltipVisible: !this.isPasswordValid(value),
    });
  };

  handleConfirmPasswordChange = ({ value }: { value: string }) => {
    const { newPassword } = this.state;
    this.setErrorsConfirmPassword({ confirmPassword: value, newPassword });
    this.setState({ confirmPassword: value });
  };

  handlePasswordSubmitSuccess = () => {
    const { locale } = this.props;
    toast.success(locale.myAccount.passwordChangedMsg, {
      autoClose: FAST_NOTIFICATION_DURATION,
    });
    this.setState({
      confirmPassword: '',
      newPassword: '',
      validPasswordLength: false,
      passwordIncludesNumber: false,
      passwordIncludesSpecialChar: false,
      passwordIncludesUppercase: false,
      passwordIncludesLowercase: false,
    });
  };

  handleSubmit = () => {
    AnalyticsLogger.changePassword();
    const { locale } = this.props;
    const { newPassword } = this.state;
    // If there is no new password errors make API call to update password
    CallhomeApi()
      .changePassword(newPassword)
      .then(() => {
        this.handlePasswordSubmitSuccess();
      })
      .catch(() => {
        toast.error(locale.error.requestFailed, { autoClose: FAST_NOTIFICATION_DURATION });
      });
  };

  handleMfaToggle = (enable = true) => {
    const {
      locale,
      user: { userId },
    } = this.props;

    const callback = () => {
      this.setState({ isMfaEnabled: enable });
      toast.success(
        enable ? locale.myAccount.enableMfaSuccess : locale.myAccount.disableMfaSuccess,
        { autoClose: FAST_NOTIFICATION_DURATION },
      );
    };

    const errorCallback = () =>
      toast.error(enable ? locale.myAccount.enableMfaFailed : locale.myAccount.disableMfaFailed, {
        autoClose: FAST_NOTIFICATION_DURATION,
      });

    UserActions.updateMfa({
      userId,
      data: { isMfaEnabled: enable },
      callback,
      errorCallback,
    });
  };

  setPasswordValidationTooltipVisible = () => {
    const { newPassword } = this.state;
    if (!this.isPasswordValid(newPassword) || !newPassword)
      this.setState({ passwordValidationTooltipVisible: true });
  };

  setPasswordValidationTooltipHidden = () =>
    this.setState({ passwordValidationTooltipVisible: false });

  isPasswordValid = (password: string) => {
    const { authSettings } = this.props;
    const minPassLength = get(authSettings, 'minPasswordLength', PASSWORD_MIN_LENGTH);
    return (
      password.length >= minPassLength &&
      REGEX_INCLUDES_NUMBER.test(password) &&
      REGEX_INCLUDES_SPECIAL_CHARACTER.test(password) &&
      REGEX_INCLUDES_UPPERCASE_LETTER.test(password) &&
      REGEX_INCLUDES_LOWERCASE_LETTER.test(password)
    );
  };

  newPasswordOnFocus = () => {
    this.setPasswordValidationTooltipVisible();
  };

  newPasswordOnBlur = () => {
    this.setPasswordValidationTooltipHidden();
  };

  setErrorsNewPassword = ({ newPassword }: { newPassword: string }) => {
    const { authSettings, locale } = this.props;
    const minPassLength = get(authSettings, 'minPasswordLength', PASSWORD_MIN_LENGTH);
    const newPasswordError = [];
    if (newPassword) {
      if (newPassword.length < minPassLength) {
        newPasswordError.push(
          `* ${getLocalizedStr({
            string: locale.error.passwordLengthValidation,
            replacements: { min: minPassLength },
          })}`,
        );
        newPasswordError.push(<br />);
      }
      if (!REGEX_INCLUDES_NUMBER.test(newPassword)) {
        newPasswordError.push(`* ${locale.error.passwordNumberValidation}`);
        newPasswordError.push(<br />);
      }
      if (!REGEX_INCLUDES_SPECIAL_CHARACTER.test(newPassword)) {
        newPasswordError.push(`* ${locale.error.passwordSpecialCharacterValidation}`);
        newPasswordError.push(<br />);
      }
      if (!REGEX_INCLUDES_UPPERCASE_LETTER.test(newPassword)) {
        newPasswordError.push(`* ${locale.error.passwordUppercaseLetterValidation}`);
        newPasswordError.push(<br />);
      }
      if (!REGEX_INCLUDES_LOWERCASE_LETTER.test(newPassword)) {
        newPasswordError.push(`* ${locale.error.passwordLowercaseLetterValidation}`);
        newPasswordError.push(<br />);
      }
    }
    this.setState({ newPasswordError: newPasswordError.length > 0 ? newPasswordError : '' });
  };

  setErrorsConfirmPassword = ({
    newPassword,
    confirmPassword,
  }: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    const { locale } = this.props;
    if (newPassword !== confirmPassword && confirmPassword) {
      this.setState({ confirmPasswordError: `* ${locale.error.passwordDoNotMatch}` });
    } else {
      this.setState({ confirmPasswordError: '' });
    }
  };

  render() {
    const { locale, popover, authSettings, user } = this.props;
    const {
      confirmPassword,
      newPassword,
      newPasswordError,
      confirmPasswordError,
      isMfaEnabled,
      validPasswordLength,
      passwordIncludesNumber,
      passwordIncludesSpecialChar,
      passwordIncludesUppercase,
      passwordIncludesLowercase,
      passwordValidationTooltipVisible,
    } = this.state;

    const mfaStateDescription = isMfaEnabled
      ? locale.myAccount.mfaEnabledDescription
      : locale.myAccount.mfsDisabledDescription;

    const minPassLength = get(authSettings, 'minPasswordLength', PASSWORD_MIN_LENGTH);

    const validations = [
      {
        text: getLocalizedStr({
          string: locale.error.passwordLengthValidation,
          replacements: { min: minPassLength },
        }),
        validated: validPasswordLength,
      },
      { text: locale.error.passwordNumberValidation, validated: passwordIncludesNumber },
      {
        text: locale.error.passwordSpecialCharacterValidation,
        validated: passwordIncludesSpecialChar,
      },
      {
        text: locale.error.passwordUppercaseLetterValidation,
        validated: passwordIncludesUppercase,
      },
      {
        text: locale.error.passwordLowercaseLetterValidation,
        validated: passwordIncludesLowercase,
      },
    ];

    const disabledSubmit =
      !validPasswordLength ||
      !passwordIncludesNumber ||
      !passwordIncludesSpecialChar ||
      !passwordIncludesLowercase ||
      !passwordIncludesUppercase ||
      newPassword !== confirmPassword;

    return (
      <div className="padded container">
        <h1>{locale.settingsPage.myAccount}</h1>
        {userSignsInWithSso(user) ? (
          <Card className="narrow" type="section">
            <CardHeader title={locale.myAccount.authenticationTitle} />
            <p>{locale.myAccount.identityProviderManagedDescription}</p>
          </Card>
        ) : (
          <>
            <Card className="narrow" type="section">
              <CardHeader title={locale.myAccount.changePassword} />
              <TextField
                dataTestId="new-password"
                errorText={newPasswordError as string}
                labelText={locale.myAccount.newPassword}
                name="new-password"
                onChange={this.handleNewPasswordChange}
                type="password"
                value={newPassword}
                className="password-textfield"
                refFunc={popover.setTriggerRef}
                onFocus={this.newPasswordOnFocus}
                onBlur={this.newPasswordOnBlur}
                autoComplete={'off'}
                inline
              />
              <PopoverTooltip {...popover} visible={passwordValidationTooltipVisible} mode="light">
                <Validations validations={validations} />
              </PopoverTooltip>
              <TextField
                dataTestId="confirm-password"
                errorText={confirmPasswordError}
                labelText={locale.myAccount.confirmPassword}
                name="confirm-password"
                onChange={this.handleConfirmPasswordChange}
                type="password"
                value={confirmPassword}
                className="password-textfield"
                autoComplete={'off'}
                inline
              />
              <Flex justifyContent="flex-end">
                <Button
                  dataTestId="password-submit"
                  text={locale.general.submit}
                  onClick={this.handleSubmit}
                  disabled={disabledSubmit}
                />
              </Flex>
            </Card>
            <Card className="narrow" type="section">
              <CardHeader
                title={locale.myAccount.mfaTitle}
                subTitle={locale.myAccount.mfaSubTitle}
              />
              <p>{mfaStateDescription}</p>
              <p>{locale.myAccount.mfaDescription}</p>
              <Flex justifyContent="flex-end">
                {!isMfaEnabled && (
                  <Button
                    text={locale.myAccount.enableMfa}
                    onClick={() => this.handleMfaToggle(true)}
                    dataTestId="enable2FA"
                  />
                )}
                {isMfaEnabled && (
                  <Button
                    text={locale.myAccount.disableMfa}
                    onClick={() => this.handleMfaToggle(false)}
                    dataTestId="disable2FA"
                  />
                )}
              </Flex>
            </Card>
          </>
        )}
      </div>
    );
  }
}

const ComposedMyAccountPage: React.FC<ComposedMyAccountPageProps> = (props) => {
  const locale = useLocale();
  const popover = usePopoverTooltip({ placement: 'bottom' });
  const user = useUserSession();
  const {
    authData: { authSettings },
  } = useContext(CommonContext);

  return (
    <MyAccountPage
      {...props}
      user={user}
      authSettings={authSettings}
      locale={locale}
      popover={popover}
    />
  );
};

export default ComposedMyAccountPage;
